<script lang="ts" setup>
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import type { UspCardStoryblok, UspSectionStoryblok } from '@/storyblok/component-types-sb'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'
  import '@/assets/css/swiper.css'

  const props = defineProps<{
    blok: UspSectionStoryblok
  }>()

  const { isMobile, isDesktop } = useBTBreakpoints()
  const VISIBLE_CARDS_COUNT = 4
  const CAROUSEL_NEXT = 'usp-carousel-next'
  const CAROUSEL_PREV = 'usp-carousel-prev'
  const CAROUSEL_PAGINATION = 'usp-carousel-pagination'

  const cards = useFilterVisibilityByCountry<UspCardStoryblok>(() => props.blok.uspCards)

  const visibleCardsCount = ref(VISIBLE_CARDS_COUNT)

  const canLoadMore = computed(() => {
    return visibleCardsCount.value < cards.value.length
  })

  const hasDesktopSliderControls = computed(() => {
    return cards.value.length > VISIBLE_CARDS_COUNT
  })

  const desktopSliderNavigation = computed(() => {
    if (!hasDesktopSliderControls.value) {
      return false
    }

    return {
      nextEl: `#${CAROUSEL_NEXT}-${props.blok._uid}`,
      prevEl: `#${CAROUSEL_PREV}-${props.blok._uid}`
    }
  })

  const desktopSliderPagination = computed(() => {
    if (!hasDesktopSliderControls.value) {
      return false
    }

    return {
      el: `#${CAROUSEL_PAGINATION}-${props.blok._uid}`,
      clickable: true,
      bulletActiveClass: 'border-bt-blue-dark',
      renderBullet: (index: number, className: string) => {
        return `<span class="${className} w-8 lg:w-14 border-b-4 border-border block h-1 cursor-pointer"></span>`
      }
    }
  })

  const desktopSliderSettings = computed<SwiperSettings>(() => ({
    speed: 500,
    keyboard: true,
    slidesPerView: VISIBLE_CARDS_COUNT,
    slidesPerGroup: VISIBLE_CARDS_COUNT,
    navigation: desktopSliderNavigation.value,
    pagination: desktopSliderPagination.value,
    spaceBetween: 24,
    loop: false,
    watchSlidesProgress: true
  }))

  const desktopSliderKey = computed(() => {
    return `${props.blok._uid}-${cards.value.length}-${hasDesktopSliderControls.value ? 'slider' : 'static'}`
  })

  function loadMoreCards() {
    visibleCardsCount.value = Math.min(visibleCardsCount.value + VISIBLE_CARDS_COUNT, cards.value.length)
  }

  const section = ref<HTMLElement | null>(null)
  const { pushSellingPointsSectionDataLayer } = useIndexPageDataLayer()

  onElementVisibleAtThreshold(section, () =>
    pushSellingPointsSectionDataLayer({
      event_action: 'impressions'
    })
  )
</script>

<template>
  <div
    v-if="cards.length"
    ref="section"
    class="mb-12 lg:mb-16 xl:mb-18"
  >
    <h2 class="mb-8 sm:mb-2">
      {{ blok.title }}
    </h2>
    <div
      v-if="!isMobile"
      class="mb-8 text-body-1"
    >
      {{ blok.text }}
    </div>
    <div
      v-if="!isDesktop"
      class="grid grid-cols-2 gap-4 sm:grid-cols-4 md:gap-6"
    >
      <div
        v-for="(card, index) in cards"
        :key="card._uid"
        class="h-full"
        :class="{
          'hidden lg:block': index >= visibleCardsCount
        }"
      >
        <StoryblokComponent :blok="card" />
      </div>
    </div>

    <div
      v-if="!isDesktop && canLoadMore"
      class="mt-6 flex justify-center"
    >
      <UiButton
        variant="link"
        :title="$t('showMore')"
        @click="loadMoreCards"
      />
    </div>

    <div
      v-if="isDesktop"
      class="relative w-full"
    >
      <div class="container">
        <div class="usp-slider-viewport -m-6 p-6">
          <div class="usp-slider relative">
            <LazyUiSliderComponent
              :key="desktopSliderKey"
              :slides="cards"
              :slider-settings="desktopSliderSettings"
              slide-class="flex h-auto"
            >
              <template #slide="{ slide }">
                <StoryblokComponent
                  v-if="typeof slide !== 'string'"
                  :key="slide._uid"
                  :blok="slide"
                />
              </template>

              <template #slider-container-end>
                <div
                  v-if="hasDesktopSliderControls"
                  :id="`${CAROUSEL_PAGINATION}-${blok._uid}`"
                  class="mt-12 flex justify-center gap-4"
                />
              </template>

              <template #slider-outside>
                <UiSliderNavigationRoundArrows
                  v-if="hasDesktopSliderControls"
                  class="pointer-events-none absolute -inset-x-4 top-[calc(50%_-_2.25rem)] z-2 hidden justify-between min-[1280px]:flex min-[1416px]:hidden min-[1552px]:flex [&_button:first-child]:-translate-x-16 xl:[&_button:first-child]:-translate-x-12 [&_button:last-child]:translate-x-16 xl:[&_button:last-child]:translate-x-12 [&_button]:pointer-events-auto"
                  :prev-btn-id="`${CAROUSEL_PREV}-${blok._uid}`"
                  :next-btn-id="`${CAROUSEL_NEXT}-${blok._uid}`"
                />
              </template>
            </LazyUiSliderComponent>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .usp-slider-viewport {
    overflow-x: clip;
    overflow-clip-margin: 0.5rem;
    overflow-y: visible;
  }

  :deep(.usp-slider) {
    swiper-container::part(container) {
      @apply overflow-visible;
    }

    swiper-slide:not(.swiper-slide-visible) {
      visibility: hidden;
    }
  }
</style>
